/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import {
  accessAPI,
  downloadFile,
  formatFechaHora,
  Boton,
  CampoInformacion
} from "../../shared";

/**
 * Contenido completo de un correo de la casilla del historial: remitente,
 * destinatarios, texto y adjuntos para descargar. Se usa dentro de un Sidebar.
 *
 * @param {Object} props
 * @param {number} props.correoId
 * @param {Object} props.toast - ref de Toast para avisar errores de descarga
 */
export default function VisorCorreo({ correoId, toast }) {
  const [correo, setCorreo] = useState(null);
  const [descargando, setDescargando] = useState(null);

  useEffect(() => {
    setCorreo(null);
    if (!correoId) return;
    accessAPI(
      "GET",
      `admin/comunicacioncliente/correo/${correoId}`,
      null,
      (respuesta) => setCorreo(respuesta),
      () =>
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo cargar el correo"
        })
    );
  }, [correoId, toast]);

  function descargar(adjunto) {
    setDescargando(adjunto.archivoId);
    downloadFile(
      `admin/archivo/${adjunto.archivoId}`,
      adjunto.nombre,
      () => setDescargando(null),
      () => {
        setDescargando(null);
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo descargar el adjunto"
        });
      }
    );
  }

  if (!correo) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "1em" }}>
        <Skeleton width="80%" height="1.5rem" />
        <Skeleton width="100%" height="20rem" />
      </div>
    );
  }

  return (
    <div>
      <h3 css={{ marginTop: 0 }}>{correo.asunto || "(sin asunto)"}</h3>
      <CampoInformacion nombreCampo="Fecha">{formatFechaHora(correo.fecha || correo.fechaRecepcion)}</CampoInformacion>
      <CampoInformacion nombreCampo="De">{correo.de}</CampoInformacion>
      <CampoInformacion nombreCampo="Para">{correo.para.join(", ") || "-"}</CampoInformacion>
      {correo.cc.length > 0 && <CampoInformacion nombreCampo="CC">{correo.cc.join(", ")}</CampoInformacion>}
      {correo.adjuntos.length > 0 && (
        <div css={{ marginTop: "1em" }}>
          <strong>Adjuntos</strong>
          <div css={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {correo.adjuntos.map((adjunto) => (
              <Boton
                key={adjunto.archivoId}
                texto
                negro
                chico
                loader={descargando === adjunto.archivoId}
                callback={() => descargar(adjunto)}
              >
                {adjunto.nombre}
              </Boton>
            ))}
          </div>
        </div>
      )}
      <Divider />
      <div css={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.9em" }}>
        {correo.texto || "(sin texto)"}
      </div>
    </div>
  );
}
